import { Injectable, signal } from "@angular/core";
import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { compare } from "compare-versions";
import { AppUpdate } from "@capawesome/capacitor-app-update";
import { Capacitor } from "@capacitor/core";
import { filter } from "rxjs";
import { LogService } from "./log.service";
import { BaseService } from "./base.service";
import { handlePromise } from "../../utils";

@Injectable({ providedIn: "root" })
export class UpdateService extends BaseService {
  public updateAvailable = signal<boolean>(false);

  private currentVersion = signal<string>("1.0.0");

  private latestVersion = signal<string>("1.0.0");

  private currentBuild = signal<string>("");

  private latestBuild = signal<string>("");

  public isIOS = Capacitor.getPlatform() === "ios";

  constructor(
    private swUpdate: SwUpdate,
    logService: LogService,
  ) {
    super(logService, "UpdateService");
    if (this.swUpdate.isEnabled) {
      swUpdate.versionUpdates
        .pipe(
          filter(
            (evt): evt is VersionReadyEvent => evt.type === "VERSION_READY",
          ),
        )
        .subscribe((evt: any) => {
          this.updateAvailable.set(true);
          this.currentVersion.set(evt.currentVersion.appData.version);
          this.latestVersion.set(evt.latestVersion.appData.version);
          this.currentBuild.set(evt.currentVersion.appData.build);
          this.latestBuild.set(evt.latestVersion.appData.build);
        });
    }
  }

  public async update() {
    if (this.isIOS) {
      await AppUpdate.openAppStore();
    } else if (this.swUpdate.isEnabled) {
      document.location.reload();
    }
  }

  public async checkForUpdate() {
    if (this.isIOS) {
      const [err, info] = await handlePromise(AppUpdate.getAppUpdateInfo());
      if (err) {
        this.logger.error("Failed to get app update info", err);
        return {
          updateAvailable: false,
          currentVersion: this.currentVersion(),
          latestVersion: this.latestVersion(),
          currentBuild: this.currentBuild(),
          latestBuild: this.latestBuild(),
        };
      }
      this.latestVersion.set(info?.availableVersionName || "1.0.0");
      this.currentVersion.set(info?.currentVersionName || "1.0.0");
      if (compare(this.latestVersion(), this.currentVersion(), ">")) {
        this.updateAvailable.set(true);
        return {
          updateAvailable: true,
          currentVersion: this.currentVersion(),
          latestVersion: this.latestVersion(),
        };
      }
    }
    if (this.swUpdate.isEnabled) {
      const versionHashHasChanged = await this.swUpdate.checkForUpdate();
      if (versionHashHasChanged) {
        this.updateAvailable.set(versionHashHasChanged);
        return {
          updateAvailable: true,
          currentVersion: this.currentVersion(),
          latestVersion: this.latestVersion(),
          currentBuild: this.currentBuild(),
          latestBuild: this.latestBuild(),
        };
      }
    }
    return {
      updateAvailable: false,
      currentVersion: this.currentVersion(),
      latestVersion: this.latestVersion(),
      currentBuild: this.currentBuild(),
      latestBuild: this.latestBuild(),
    };
  }
}
